import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useNoticias } from "../hooks/useCms";
import { mediaUrl } from "../lib/strapi";
import type { Noticia } from "../types/cms";

/**
 * Listado de noticias: /noticias
 *
 * Las noticias destacadas aparecen primero (un poco más grandes); el
 * resto se muestra en una rejilla cronológica descendente. Si no hay
 * noticias publicadas en el CMS mostramos un mensaje neutro — la web no
 * rompe.
 */

const etiquetaLabel: Record<NonNullable<Noticia["etiquetas"]>, string> = {
  programa: "Programa",
  evento: "Evento",
  prensa: "Prensa",
  convocatoria: "Convocatoria",
};

export function NoticiasPage() {
  const { data: noticias, isLoading } = useNoticias(48);

  // Separamos destacadas. La API no permite ordenar por destacada+fecha en una
  // sola pasada con boolean, así que lo hacemos en cliente: ya tenemos la
  // colección entera y son pocas.
  const destacadas = (noticias ?? []).filter((n) => n.destacada);
  const resto = (noticias ?? []).filter((n) => !n.destacada);

  return (
    <>
      <Helmet>
        <title>Noticias — Campus EMJC</title>
        <meta
          name="description"
          content="Últimas noticias y novedades de la Escuela Municipal de Jóvenes Científicos de Extremadura: convocatorias, prensa, eventos y avances de los programas."
        />
      </Helmet>

      <section className="container-emjc py-20">
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
          Noticias
        </p>
        <h1 className="mt-2 max-w-3xl font-display text-5xl font-semibold leading-tight md:text-6xl">
          Lo último en la EMJC.
        </h1>
        <p className="mt-6 max-w-3xl text-lg text-gray-700">
          Convocatorias abiertas, prensa, salidas y novedades de los
          programas. Ordenadas de más recientes a más antiguas.
        </p>

        {isLoading && (
          <p className="mt-12 text-gray-700">Cargando noticias…</p>
        )}

        {!isLoading && (!noticias || noticias.length === 0) && (
          <div className="mt-12 rounded-2xl border border-gray-300 bg-white p-8">
            <p className="font-display text-xl font-semibold">
              Aún no hay noticias publicadas.
            </p>
            <p className="mt-3 text-gray-700">
              Vuelve pronto — publicamos novedades cada vez que abrimos una
              convocatoria nueva o aparecemos en prensa.
            </p>
          </div>
        )}

        {destacadas.length > 0 && (
          <ul className="mt-12 grid gap-8 md:grid-cols-2">
            {destacadas.map((n) => (
              <li key={n.documentId ?? n.id}>
                <NoticiaCard noticia={n} variant="destacada" />
              </li>
            ))}
          </ul>
        )}

        {resto.length > 0 && (
          <ul className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {resto.map((n) => (
              <li key={n.documentId ?? n.id}>
                <NoticiaCard noticia={n} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function NoticiaCard({
  noticia,
  variant,
}: {
  noticia: Noticia;
  variant?: "destacada";
}) {
  const portadaSrc =
    mediaUrl(noticia.imagenPortada?.formats?.medium?.url) ??
    mediaUrl(noticia.imagenPortada?.url);

  const fechaTxt = new Date(noticia.fechaPublicacion).toLocaleDateString(
    "es-ES",
    { day: "numeric", month: "long", year: "numeric" },
  );

  const etiqueta = noticia.etiquetas ? etiquetaLabel[noticia.etiquetas] : null;

  const isDestacada = variant === "destacada";

  return (
    <Link
      to={`/noticias/${noticia.slug}`}
      className="group block overflow-hidden rounded-2xl border border-gray-300 bg-white transition hover:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
    >
      {portadaSrc && (
        <div
          className={`w-full overflow-hidden bg-brand-paper ${
            isDestacada ? "aspect-[16/9]" : "aspect-[4/3]"
          }`}
        >
          <img
            src={portadaSrc}
            alt={noticia.imagenPortada?.alternativeText ?? noticia.titulo}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs uppercase tracking-widest text-gray-700">
          <time dateTime={noticia.fechaPublicacion}>{fechaTxt}</time>
          {etiqueta && (
            <span className="rounded-full bg-brand-orange-soft px-2 py-0.5 text-brand-orange-deep">
              {etiqueta}
            </span>
          )}
          {noticia.destacada && (
            <span className="rounded-full bg-accent-sun px-2 py-0.5 text-brand-ink">
              Destacada
            </span>
          )}
        </div>
        <h2
          className={`mt-2 font-display font-semibold leading-tight ${
            isDestacada ? "text-2xl md:text-3xl" : "text-xl"
          }`}
        >
          {noticia.titulo}
        </h2>
        {noticia.resumen && (
          <p className="mt-3 line-clamp-3 text-sm text-gray-700">
            {noticia.resumen}
          </p>
        )}
        <p className="mt-4 text-sm font-semibold text-brand-orange-deep">
          Leer noticia →
        </p>
      </div>
    </Link>
  );
}
